import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Grid,
  Alert,
  Fade,
  Chip,
  Stack
} from '@mui/material';
import { AccessTime, Save, Cancel, CheckCircle, Warning } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/redux/hooks';
import {
  gettimeConfiguration,
  timeConfiguration
} from '../../../../redux/reducer/autoPlannerSlices/autoplannerConfigurationSlice';
import CustomButton from '../../../../../../common/components/buttons/CustomButton';

interface TimeConfig {
  hours: number;
  minutes: number;
  seconds: number;
  period: 'AM' | 'PM';
}

const BookingDeadlineConfig = ({ setIsTime }: any) => {
  const [selectedTime, setSelectedTime] = useState<TimeConfig>({
    hours: 12,
    minutes: 0,
    seconds: 0,
    period: 'AM'
  });

  const [savedTime, setSavedTime] = useState<TimeConfig | null>(null);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const timeLoading = useAppSelector((state: any) => state.TimeConfiguration?.isLoading);

  const dispatch = useAppDispatch();

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutesSeconds = Array.from({ length: 60 }, (_, i) => i);

  const handleTimeChange = (field: keyof TimeConfig, value: number | string) => {
    setSelectedTime(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatTime = (timeObj: TimeConfig = selectedTime): string => {
    const { hours, minutes, seconds, period } = timeObj;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      // .padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${period}`;
      .padStart(2, '0')} ${period}`;
  };

  const handleUpdate = (): void => {
    setIsEditMode(true);
  };

  function convertSecondsToTime(totalSeconds: number): TimeConfig {
    let hours24 = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let period: 'AM' | 'PM' = 'AM';
    let hours = hours24;

    if (hours24 === 0) {
      hours = 12;
      period = 'AM';
    } else if (hours24 === 12) {
      hours = 12;
      period = 'PM';
    } else if (hours24 > 12) {
      hours = hours24 - 12;
      period = 'PM';
    } else {
      period = 'AM';
    }

    return {
      hours,
      minutes,
      seconds,
      period
    };
  }

  function getTotalSeconds(timeObj: any) {
    let { hours, minutes, seconds, period } = timeObj;

    if (period === 'AM' && hours === 12) {
      hours = 0;
    } else if (period === 'PM' && hours !== 12) {
      hours += 12;
    }

    return hours * 3600 + minutes * 60 + seconds;
  }

  const handleSave = async (params: any) => {
    const totalSeconds = getTotalSeconds(params);
    setSavedTime(selectedTime);
    setShowSuccess(true);
    setIsEditMode(false);
    const res = await dispatch(timeConfiguration(totalSeconds));
    if (res?.meta?.requestStatus === 'fulfilled') {
      setIsTime(false);
    }
  };

  const handleCancel = (): void => {
    if (savedTime) {
      setSelectedTime(savedTime);
    } else {
      setSelectedTime({
        hours: 12,
        minutes: 0,
        seconds: 0,
        period: 'AM'
      });
    }
    setIsEditMode(false);
  };

  useEffect(() => {
    dispatch(gettimeConfiguration());
  }, []);

  useEffect(() => {
    dispatch(gettimeConfiguration()).then((res: any) => {
      if (res?.payload?.data?.cutoffTime !== undefined) {
        const timeObj = convertSecondsToTime(res.payload.data.cutoffTime);
        setSelectedTime(timeObj);
        setSavedTime(timeObj);
      }
    });
  }, [dispatch]);

  return (
    <Box sx={{ maxWidth: 400, p: 2 }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Header */}
        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
          <AccessTime color='primary' />
          <Box>
            <Typography variant='h6' component='h1' fontWeight={600}>
              Booking Deadline
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Agents can only book trips before this time
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <FormControl fullWidth size='small'>
              <InputLabel>Hours</InputLabel>
              <Select
                value={selectedTime.hours}
                label='Hours'
                // disabled={!isEditMode}
                onChange={e => handleTimeChange('hours', Number(e.target.value))}
              >
                {hours.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour.toString().padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Minutes */}
          <Grid item xs={4}>
            <FormControl fullWidth size='small'>
              <InputLabel>Min</InputLabel>
              <Select
                value={selectedTime.minutes}
                label='Min'
                // disabled={!isEditMode}
                onChange={e => handleTimeChange('minutes', Number(e.target.value))}
              >
                {minutesSeconds.map(minute => (
                  <MenuItem key={minute} value={minute}>
                    {minute.toString().padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* 
          <Grid item xs={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>Sec</InputLabel>
              <Select
                value={selectedTime.seconds}
                label='Sec'
                disabled={!isEditMode}
                onChange={e => handleTimeChange('seconds', Number(e.target.value))}
              >
                {minutesSeconds.map(second => (
                  <MenuItem key={second} value={second}>
                    {second.toString().padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={4}>
            <FormControl fullWidth size='small'>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedTime.period}
                label='Period'
                // disabled={!isEditMode}
                onChange={e => handleTimeChange('period', e.target.value)}
              >
                <MenuItem value='AM'>AM</MenuItem>
                <MenuItem value='PM'>PM</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'primary.50',
            borderColor: 'primary.200'
          }}
        >
          <Typography
            variant='caption'
            color='primary.main'
            fontWeight={500}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            Booking Cutoff Time
          </Typography>
          <Typography
            variant='h4'
            component='div'
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              color: 'primary.dark',
              my: 1,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {formatTime()}
          </Typography>
          <Chip
            icon={<Warning />}
            label='No trip bookings allowed after this time'
            size='small'
            color='warning'
            variant='outlined'
          />
        </Paper>

        <Box mt={3} sx={{ textAlign: 'right' }}>
          {/* {!isEditMode ? ( */}
          {/* <>
            <CustomButton
              category='Close'
              className='cancel'
              sx={{ marginRight: '10px' }}
              onClick={() => {
                setIsTime(false);
              }}
            />
            <CustomButton
              category='Update'
              className='saveChanges'
              onClick={handleUpdate}
            />
          </> */}
          <>
            <CustomButton
              category='Cancel'
              className='cancel'
              sx={{ marginRight: '10px' }}
              onClick={() => {
                setIsTime(false);
              }}
            />
            <CustomButton
              category='Save'
              className='saveChanges'
              type='submit'
              loading={timeLoading}
              onClick={() => handleSave(selectedTime)}
            />
          </>
        </Box>
      </Paper>
    </Box>
  );
};

export default BookingDeadlineConfig;
